/**
 * Queries and derived views.
 *
 * The browser runs the same `buildTimeline`/`summarize` the collector does, so a
 * number on screen can never disagree with a stored rollup over the same window.
 */

import { collection, getDocs, limit, orderBy, query, where } from 'firebase/firestore';
import { buildTimeline, summarize } from '@netwatch/shared';
import type { Classification, Heartbeat, Outage, Rollup, Sample, UptimeSummary } from '@netwatch/shared';
import { firestore } from './firebase.js';

export const RANGES = {
  '24h': 24 * 3_600_000,
  '7d': 7 * 24 * 3_600_000,
  '30d': 30 * 24 * 3_600_000,
} as const;

export type RangeKey = keyof typeof RANGES;

export const CLASS_META: Record<Classification, { label: string; color: string; icon: string; countsAsDowntime: boolean }> = {
  up: { label: 'Up', color: 'var(--status-good)', icon: '●', countsAsDowntime: false },
  isp_outage: { label: 'Internet down', color: 'var(--status-critical)', icon: '▲', countsAsDowntime: true },
  local_network: { label: 'House-side fault', color: 'var(--status-serious)', icon: '◆', countsAsDowntime: true },
  reporting_gap: { label: 'Reporting hiccup', color: 'var(--status-warning)', icon: '◇', countsAsDowntime: false },
  // Neutral by design — absence of evidence, not a fault.
  monitoring_gap: { label: 'Not measured', color: 'var(--status-unknown)', icon: '○', countsAsDowntime: false },
};

/**
 * Firestore rejects any structured query with a limit above this. It is a hard
 * server-side cap, not a tunable — a 30-day range at one sample a minute is
 * ~43,200 rows and can never be served raw. That is what the rollups are for;
 * see `RAW_RANGE_MS` below.
 */
const MAX_QUERY_LIMIT = 10_000;

/**
 * Longest window still served from raw samples.
 *
 * 24h ≈ 1,440 samples — comfortably under the cap and worth the minute-level
 * detail. Anything longer reads hourly rollups instead: raw samples only live
 * 14 days anyway, so a 30-day view could never be answered from them.
 */
export const RAW_RANGE_MS = RANGES['24h'];

export async function fetchSamples(from: number, to: number): Promise<Sample[]> {
  const q = query(
    collection(firestore, 'samples'),
    where('ts', '>=', from),
    where('ts', '<', to),
    orderBy('ts', 'asc'),
    limit(MAX_QUERY_LIMIT),
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => d.data() as Sample);
}

export async function fetchRollups(from: number, granularity: 'hour' | 'day'): Promise<Rollup[]> {
  const q = query(
    collection(firestore, 'rollups'),
    where('granularity', '==', granularity),
    where('startTs', '>=', from),
    orderBy('startTs', 'asc'),
    limit(2000),
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => d.data() as Rollup);
}

/**
 * Uptime over a set of hourly rollups.
 *
 * Each rollup already carries `msByClass` computed by the collector using the
 * same `buildTimeline`/`summarize` the 24h view runs in the browser, so summing
 * them cannot drift from the raw-sample answer. Hours with no rollup at all are
 * counted as unmonitored rather than skipped — otherwise a stretch where the
 * collector never ran would quietly vanish from the denominator and inflate
 * coverage, which is the same lie in a different place.
 */
export function summaryFromRollups(rollups: Rollup[], from: number, to: number): UptimeSummary {
  const msByClass: Record<Classification, number> = {
    up: 0,
    isp_outage: 0,
    local_network: 0,
    monitoring_gap: 0,
    reporting_gap: 0,
  };

  let accounted = 0;
  for (const r of rollups) {
    for (const [k, v] of Object.entries(r.msByClass ?? {})) {
      msByClass[k as Classification] += v ?? 0;
      accounted += v ?? 0;
    }
  }

  const windowMs = Math.max(0, to - from);
  const missing = Math.max(0, windowMs - accounted);
  msByClass.monitoring_gap += missing;

  const upMs = msByClass.up + msByClass.reporting_gap;
  const downMs = msByClass.isp_outage + msByClass.local_network;
  const monitoredMs = upMs + downMs;
  const unmonitoredMs = msByClass.monitoring_gap;

  return {
    windowMs: monitoredMs + unmonitoredMs,
    monitoredMs,
    upMs,
    downMs,
    unmonitoredMs,
    msByClass,
    uptimePct: monitoredMs > 0 ? (upMs / monitoredMs) * 100 : null,
    coveragePct: windowMs > 0 ? (monitoredMs / windowMs) * 100 : 0,
  };
}

/** Chart points from hourly rollups, keyed on each bucket's start. */
export function rollupPoints(rollups: Rollup[], pick: (r: Rollup) => number | null): { x: number; y: number | null }[] {
  return rollups.map((r) => ({ x: r.startTs, y: pick(r) }));
}

/** Heatmap cells from hourly rollups — one bucket is already one cell. */
export function rollupHeatmapCells(
  rollups: Rollup[],
  pick: (r: Rollup) => number | null,
): { day: number; hour: number; value: number | null }[] {
  const acc = new Map<string, number[]>();
  for (const r of rollups) {
    const d = new Date(r.startTs);
    const v = pick(r);
    if (v === null || !Number.isFinite(v)) continue;
    const key = `${d.getDay()}-${d.getHours()}`;
    if (!acc.has(key)) acc.set(key, []);
    acc.get(key)!.push(v);
  }
  const cells: { day: number; hour: number; value: number | null }[] = [];
  for (let day = 0; day < 7; day++) {
    for (let hour = 0; hour < 24; hour++) {
      const vals = acc.get(`${day}-${hour}`);
      cells.push({ day, hour, value: vals?.length ? vals.reduce((a, b) => a + b, 0) / vals.length : null });
    }
  }
  return cells;
}

export async function fetchOutages(from: number): Promise<Outage[]> {
  const q = query(collection(firestore, 'outages'), where('startTs', '>=', from), orderBy('startTs', 'desc'), limit(200));
  const snap = await getDocs(q);
  return snap.docs.map((d) => d.data() as Outage);
}

export async function fetchHeartbeats(): Promise<Heartbeat[]> {
  const snap = await getDocs(collection(firestore, 'heartbeats'));
  return snap.docs.map((d) => d.data() as Heartbeat);
}

/**
 * Live status.
 *
 * Deliberately three-valued. If the prober has gone quiet we do not know whether
 * the link is down — saying "down" would be the same guess this whole project
 * refuses to make — so the honest answer is "no contact".
 */
export type LiveStatus =
  | { kind: 'up'; since: number }
  | { kind: 'down'; since: number; classification: Classification }
  | { kind: 'no-contact'; lastSeen: number }
  | { kind: 'never' };

export function liveStatus(heartbeats: Heartbeat[], samples: Sample[], openOutage: Outage | undefined, now: number, staleMs: number): LiveStatus {
  if (heartbeats.length === 0) return { kind: 'never' };
  const newest = heartbeats.reduce((a, b) => (a.lastSeenTs > b.lastSeenTs ? a : b));

  if (now - newest.lastSeenTs > staleMs) {
    return { kind: 'no-contact', lastSeen: newest.lastSeenTs };
  }
  if (openOutage) {
    return { kind: 'down', since: openOutage.startTs, classification: openOutage.classification };
  }
  const last = samples[samples.length - 1];
  if (last && (!last.wanOk || !last.localOk)) {
    return { kind: 'down', since: last.ts, classification: last.localOk ? 'isp_outage' : 'local_network' };
  }
  return { kind: 'up', since: newest.newestSampleTs };
}

export function summaryFor(samples: Sample[], from: number, to: number): UptimeSummary {
  return summarize(buildTimeline(samples, from, to));
}

/** Down-samples a series to about `maxPoints` by bucketed mean, preserving nulls. */
export function downsample(
  samples: Sample[],
  pick: (s: Sample) => number | null,
  from: number,
  to: number,
  maxPoints = 220,
): { x: number; y: number | null }[] {
  if (samples.length === 0) return [];
  const bucketMs = Math.max(60_000, Math.ceil((to - from) / maxPoints));
  const buckets = new Map<number, number[]>();

  for (const s of samples) {
    const key = Math.floor(s.ts / bucketMs) * bucketMs;
    const v = pick(s);
    if (!buckets.has(key)) buckets.set(key, []);
    if (v !== null && Number.isFinite(v)) buckets.get(key)!.push(v);
  }

  const out: { x: number; y: number | null }[] = [];
  for (let t = Math.floor(from / bucketMs) * bucketMs; t < to; t += bucketMs) {
    const vals = buckets.get(t);
    // A bucket the prober never reported stays null so the line breaks rather
    // than interpolating across time nobody measured.
    out.push({ x: t, y: vals && vals.length > 0 ? vals.reduce((a, b) => a + b, 0) / vals.length : null });
  }
  return out;
}

/** Speed points only — one entry per test, not per cycle. */
export function speedPoints(samples: Sample[], key: 'downMbps' | 'upMbps'): { x: number; y: number | null }[] {
  return samples.filter((s) => s.speed).map((s) => ({ x: s.ts, y: s.speed![key] }));
}

/** Mean of a metric per (weekday, hour) cell. Null where nothing was measured. */
export function heatmapCells(samples: Sample[], pick: (s: Sample) => number | null): { day: number; hour: number; value: number | null }[] {
  const acc = new Map<string, number[]>();
  for (const s of samples) {
    const d = new Date(s.ts);
    const key = `${d.getDay()}-${d.getHours()}`;
    const v = pick(s);
    if (v === null || !Number.isFinite(v)) continue;
    if (!acc.has(key)) acc.set(key, []);
    acc.get(key)!.push(v);
  }

  const cells: { day: number; hour: number; value: number | null }[] = [];
  for (let day = 0; day < 7; day++) {
    for (let hour = 0; hour < 24; hour++) {
      const vals = acc.get(`${day}-${hour}`);
      cells.push({ day, hour, value: vals && vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : null });
    }
  }
  return cells;
}

export function formatDuration(ms: number | null): string {
  if (ms === null || !Number.isFinite(ms)) return '—';
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${s % 60}s`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ${m % 60}m`;
  return `${Math.floor(h / 24)}d ${h % 24}h`;
}

export interface OutageStats {
  count: number;
  totalMs: number;
  longestMs: number | null;
  meanMs: number | null;
}

/** Only genuine connectivity failures count — gaps are coverage, not outages. */
export function outageStats(outages: Outage[]): OutageStats {
  const real = outages.filter((o) => CLASS_META[o.classification]?.countsAsDowntime && o.durationMs !== null);
  const durations = real.map((o) => o.durationMs as number);
  const totalMs = durations.reduce((a, b) => a + b, 0);
  return {
    count: real.length,
    totalMs,
    longestMs: durations.length ? Math.max(...durations) : null,
    meanMs: durations.length ? totalMs / durations.length : null,
  };
}
